/**
 * Admin API client. All calls include credentials (cookies).
 * Errors are normalised to a single string for safe rendering.
 */
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Error desconocido.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d)))
      .filter(Boolean)
      .join(" · ");
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Auth
export const authApi = {
  me: () => api.get("/auth/me").then((r) => r.data),
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
};

// Proposals
export const proposalsApi = {
  list: (params = {}) => api.get("/admin/proposals", { params }).then((r) => r.data),
  get: (id) => api.get(`/admin/proposals/${id}`).then((r) => r.data),
  create: (payload) => api.post("/admin/proposals", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/admin/proposals/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/admin/proposals/${id}`).then((r) => r.data),
  duplicate: (id) => api.post(`/admin/proposals/${id}/duplicate`).then((r) => r.data),
  regenerateLink: (id) => api.post(`/admin/proposals/${id}/regenerate-link`).then((r) => r.data),
};

// Media
export const mediaApi = {
  list: () => api.get("/admin/media").then((r) => r.data),
  upload: (file, tag = "") => {
    const fd = new FormData();
    fd.append("file", file);
    if (tag) fd.append("tag", tag);
    return api.post("/admin/media/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  addExternal: (url, tag = "") => api.post("/admin/media/external", { url, tag }).then((r) => r.data),
  remove: (id) => api.delete(`/admin/media/${id}`).then((r) => r.data),
};

// Public — no auth
export const publicApi = {
  getProposal: (token, password) =>
    axios.get(`${API}/public/proposals/${token}`, { params: password ? { password } : {} })
      .then((r) => r.data),
  trackClick: (token) =>
    axios.post(`${API}/public/proposals/${token}/click`).catch(() => {}),
  acceptProposal: (token, payload) =>
    axios.post(`${API}/public/proposals/${token}/accept`, payload).then((r) => r.data),
};

// Payments
export const paymentsApi = {
  options: () => api.get("/admin/payments/options").then((r) => r.data),
  getSettings: () => api.get("/admin/payments/settings").then((r) => r.data),
  updateSettings: (payload) => api.put("/admin/payments/settings", payload).then((r) => r.data),
  acceptances: (proposalId) => api.get(`/admin/proposals/${proposalId}/acceptances`).then((r) => r.data),
  allAcceptances: () => api.get("/admin/payments/acceptances").then((r) => r.data),
};

// Templates
export const templatesApi = {
  list: () => api.get("/admin/templates").then((r) => r.data),
  get: (id) => api.get(`/admin/templates/${id}`).then((r) => r.data),
  create: (payload) => api.post("/admin/templates", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/admin/templates/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/admin/templates/${id}`).then((r) => r.data),
  use: (id, payload) => api.post(`/admin/templates/${id}/use`, payload).then((r) => r.data),
};

// Escapadas
export const escapadasApi = {
  list: () => api.get("/admin/escapadas").then((r) => r.data),
  get: (id) => api.get(`/admin/escapadas/${id}`).then((r) => r.data),
  create: (payload) => api.post("/admin/escapadas", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/admin/escapadas/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/admin/escapadas/${id}`).then((r) => r.data),
};

export const publicEscapadasApi = {
  list: () => api.get("/public/escapadas").then((r) => r.data),
  get: (slug) => api.get(`/public/escapadas/${slug}`).then((r) => r.data),
};

